import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addInventoryItem, getProductTypes } from '../../api/inventory';
import { DeliveryMethod, ProductInventoryItem } from 'api/inventory-types';

const AddStock = () => {
    const navigate = useNavigate();
    const { productId } = useParams();
    const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>(DeliveryMethod.VIRTUAL_PRODUCT);
    const [dataMode, setDataMode] = useState<'coupon' | 'keyvalue'>('coupon');
    const [couponCodes, setCouponCodes] = useState('');
    const [formData, setFormData] = useState({
        amount: '1',
        instanceData: '{}'
    });
    const [error, setError] = useState('');
    const [jsonError, setJsonError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });

    useEffect(() => {
        const loadProduct = async () => {
            const products = await getProductTypes();
            const product = products.find(p => p.id === productId);
            if (product) {
                setDeliveryMethod(product.deliveryMethod as DeliveryMethod);
            }
        };
        loadProduct();
    }, [productId]);

    const validateJson = (jsonString: string): boolean => {
        try {
            JSON.parse(jsonString);
            setJsonError('');
            return true;
        } catch (err) {
            setJsonError('Invalid JSON format');
            return false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (dataMode === 'coupon') {
            // Parse comma-separated coupon codes
            const codes = couponCodes
                .split(',')
                .map(code => code.trim())
                .filter(code => code.length > 0);
            
            if (codes.length === 0) {
                setError('Please enter at least one coupon code');
                return;
            }
            
            if (codes.length > 100) {
                setError('Maximum 100 coupon codes allowed per batch');
                return;
            }

            setIsProcessing(true);
            setProgress({ current: 0, total: codes.length });
            setError('');

            let successCount = 0;
            let failureCount = 0;
            const errors: string[] = [];

            // Process codes sequentially
            for (let i = 0; i < codes.length; i++) {
                const code = codes[i];
                setProgress({ current: i + 1, total: codes.length });
                
                try {
                    const instanceData = { couponCode: code };
                    const item = new ProductInventoryItem(
                        crypto.randomUUID(),
                        1, 
                        productId!, 
                        deliveryMethod,
                        instanceData,
                        new Date());

                    const response = await addInventoryItem(item);

                    if (response.status === 200) {
                        successCount++;
                    } else {
                        failureCount++;
                        const errorText = await response.text();
                        errors.push(`Code "${code}": ${errorText || 'Failed to add'}`);
                    }
                } catch (err: any) {
                    failureCount++;
                    errors.push(`Code "${code}": ${err.message}`);
                }
            }

            setIsProcessing(false);

            if (failureCount === 0) {
                navigate('/admin/products');
            } else {
                setError(
                    `Processed ${successCount} successfully, ${failureCount} failed.\n` +
                    errors.slice(0, 5).join('\n') +
                    (errors.length > 5 ? `\n... and ${errors.length - 5} more errors` : '')
                );
            }
        } else {
            // Key-value mode - keep existing behavior
            if (!validateJson(formData.instanceData)) {
                return;
            }

            const instanceData = JSON.parse(formData.instanceData);

            try {
                const item = new ProductInventoryItem(
                    crypto.randomUUID(),
                    parseInt(formData.amount), 
                    productId!, 
                    deliveryMethod,
                    instanceData,
                    new Date());

                const response = await addInventoryItem(item);

                if (response.status === 200) {
                    navigate('/admin/products');
                } else {
                    setError('Failed to add inventory items');
                }
            } catch (err: any) {
                setError(err.message);
            }
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'instanceData') {
            validateJson(value);
        }
    };

    return (
        <div className="max-w-2xl mx-auto py-8">
            <h2 className="text-2xl font-bold mb-6">Add Stock Items</h2>
            <div className="bg-gray-100 border border-gray-300 rounded p-4 mb-6">
                <p className="text-sm text-gray-600">Product ID: <span className="font-mono">{productId}</span></p>
            </div>
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex space-x-4 mb-4">
                    <button
                        type="button"
                        onClick={() => setDataMode('coupon')}
                        className={`px-4 py-2 rounded ${dataMode === 'coupon' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    >
                        Coupon Code
                    </button>
                    <button
                        type="button"
                        onClick={() => setDataMode('keyvalue')}
                        className={`px-4 py-2 rounded ${dataMode === 'keyvalue' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    >
                        Key-Value Pairs
                    </button>
                </div>

                {dataMode === 'keyvalue' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Amount
                        </label>
                        <input
                            type="number"
                            name="amount"
                            min="1"
                            required
                            value={formData.amount}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                            placeholder="1"
                        />
                    </div>
                )}

                <div>
                    {dataMode === 'coupon' ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Coupon Codes (comma-separated, up to 100)
                            </label>
                            <textarea
                                value={couponCodes}
                                onChange={(e) => setCouponCodes(e.target.value)}
                                className="w-full border border-gray-300 rounded-md shadow-sm p-2"
                                placeholder="CODE1, CODE2, CODE3, ..."
                                rows={4}
                                disabled={isProcessing}
                            />
                            <p className="mt-1 text-sm text-gray-500">
                                {couponCodes.split(',').map(c => c.trim()).filter(c => c.length > 0).length} code(s) entered
                            </p>
                            {isProcessing && (
                                <div className="mt-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-700">
                                            Processing: {progress.current} / {progress.total}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            {Math.round((progress.current / progress.total) * 100)}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div
                                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                            style={{ width: `${(progress.current / progress.total) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Instance Data (Key-Value Pairs)
                            </label>
                            {Object.keys(JSON.parse(formData.instanceData || '{}')).map((key, index) => (
                                <div key={index} className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={key}
                                        onChange={(e) => {
                                            const oldData = JSON.parse(formData.instanceData || '{}');
                                            const value = oldData[key];
                                            delete oldData[key];
                                            oldData[e.target.value] = value;
                                            setFormData({
                                                ...formData,
                                                instanceData: JSON.stringify(oldData)
                                            });
                                        }}
                                        className="flex-1 border border-gray-300 rounded-md shadow-sm p-2"
                                        placeholder="Key"
                                    />
                                    <input
                                        type="text"
                                        value={JSON.parse(formData.instanceData || '{}')[key]}
                                        onChange={(e) => {
                                            const data = JSON.parse(formData.instanceData || '{}');
                                            data[key] = e.target.value;
                                            setFormData({
                                                ...formData,
                                                instanceData: JSON.stringify(data)
                                            });
                                        }}
                                        className="flex-1 border border-gray-300 rounded-md shadow-sm p-2"
                                        placeholder="Value"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const data = JSON.parse(formData.instanceData || '{}');
                                            delete data[key];
                                            setFormData({
                                                ...formData,
                                                instanceData: JSON.stringify(data)
                                            });
                                        }}
                                        className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                    const data = JSON.parse(formData.instanceData || '{}');
                                    data[`key${Object.keys(data).length + 1}`] = '';
                                    setFormData({
                                        ...formData,
                                        instanceData: JSON.stringify(data)
                                    });
                                }}
                                className="mt-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                            >
                                Add Key-Value Pair
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/products')}
                        className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        disabled={!!jsonError || isProcessing}
                    >
                        {isProcessing ? 'Processing...' : 'Add Stock'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddStock; 